import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Template } from '../lib/api';
import type { useAuth } from '../auth/useAuth';

type Auth = ReturnType<typeof useAuth>;

export function TemplateLibraryPage({ auth }: { auth: Auth }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const load = () => {
    setBusy(true);
    api.listTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  };

  useEffect(load, []);

  const canEdit = (t: Template) => {
    if (t.is_builtin) return auth.isAdmin;
    if (auth.canEdit) return true;
    return t.created_by.toLowerCase() === auth.user?.email.toLowerCase();
  };

  const handleDelete = async (t: Template) => {
    if (!window.confirm(`Delete template "${t.name}"? (soft delete, can be restored from D1)`)) return;
    try {
      await api.deleteTemplate(t.id);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="stack">
      <header className="row">
        <div>
          <h1 style={{ marginBottom: '0.2em' }}>Template library</h1>
          <p className="muted" style={{ margin: 0 }}>
            Built-in templates ship with the tool. Saved templates are shared across the team.
          </p>
        </div>
        <span className="spacer" />
        {auth.canEdit && (
          <Link to="/templates/new" className="primary" style={{ textDecoration: 'none' }}>
            <button type="button" className="primary">+ New template</button>
          </Link>
        )}
      </header>

      {error && <div className="banner error">{error}</div>}

      <section className="card">
        {busy ? (
          <p className="muted">Loading…</p>
        ) : templates.length === 0 ? (
          <p className="muted">No templates yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Columns</th>
                <th>Created by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.name}</strong>
                    {t.is_builtin ? (
                      <> <span className="tag builtin">built-in</span></>
                    ) : null}
                  </td>
                  <td className="muted" style={{ maxWidth: 420 }}>{t.description || '—'}</td>
                  <td>{t.schema.length}</td>
                  <td className="muted">{t.created_by}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" className="primary" onClick={() => navigate(`/generate/${t.id}`)}>
                        Generate
                      </button>
                      {canEdit(t) && (
                        <button type="button" onClick={() => navigate(`/templates/${t.id}/edit`)}>
                          Edit
                        </button>
                      )}
                      {canEdit(t) && (
                        <button type="button" className="danger" onClick={() => handleDelete(t)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
